import {
  ApolloClient,
  defaultDataIdFromObject,
  from,
  InMemoryCache,
  split,
  Operation,
} from "@apollo/client"
import { authMiddleware } from "./authMiddleware"
import { addressSubjectPossibleTypes } from "./cachePossibleTypes/addressSubjectPossibleTypes"
import { auditableObjectPossibleTypes } from "./cachePossibleTypes/auditableObjectPossibleTypes"
import { customFormsPossibleTypes } from "./cachePossibleTypes/customFormsPossibleTypes"
import { nodePossibleTypes } from "./cachePossibleTypes/nodePossibleTypes"
import { paymentMethodPossibleTypes } from "./cachePossibleTypes/paymentMethodPossibleTypes"
import { typePoliciesCompanyFields } from "./cacheTypePolicies/companyFields"
import { typePoliciesCustomFormsFields } from "./cacheTypePolicies/customFormsFields"
import { typePoliciesHiringFields } from "./cacheTypePolicies/hiringFields"
import { typePoliciesQueryFields } from "./cacheTypePolicies/queryFields"
import { typePoliciesCompensationInsightsFields } from "./cacheTypePolicies/typePoliciesCompensationInsightsFields"
import { errorLink } from "./errorLink"
import { httpLink as customHttpLink } from "./httpLink"
import type { ApolloClientArgs } from "./types"
import { createConsumer } from "@rails/actioncable"
import { ActionCableLink } from "./ActionCableLink"
import { Kind, OperationTypeNode } from "graphql"
import type { OperationDefinitionNode } from "graphql/language/ast"

const cable = createConsumer()
const channelName = "ConversationChannel"

export function apolloClient({
  connectToDevTools = window.gon.reactAppEnv === "development",
  csrfToken,
  unauthenticatedRedirect,
  uri,
}: ApolloClientArgs) {
  const hasSubscriptionOperation = ({ query: { definitions } }: Operation) => {
    return (definitions as OperationDefinitionNode[]).some(
      ({ kind, operation }) =>
        kind === Kind.OPERATION_DEFINITION && (operation ?? "") === OperationTypeNode.SUBSCRIPTION,
    )
  }

  // Use split to determine which link to use (WebSocket for subscriptions, HTTP for queries/mutations)
  const splitLink = split(
    hasSubscriptionOperation,
    new ActionCableLink({ cable, channelName }),
    from([
      errorLink,
      authMiddleware({
        csrfToken,
        unauthenticatedRedirect,
      }),
      customHttpLink({ uri }),
    ]),
  )

  const client = new ApolloClient({
    cache: new InMemoryCache({
      // The normalization of the CustomFormResponses was causing
      // wrong data to be displayed in the UI. So I'll create a new ID
      // for each response, so that Apollo will not try to merge them.
      // This is a fallback to the typePolicies.keyFields, just in case
      // a new type of answer is added to the schema.
      dataIdFromObject(responseObject) {
        if (responseObject.__typename?.startsWith("CustomFormsResponse")) {
          const randomId =
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15)
          return `${responseObject.__typename}:${randomId}`
        }
        return defaultDataIdFromObject(responseObject)
      },
      possibleTypes: Object.assign(
        {},
        addressSubjectPossibleTypes,
        auditableObjectPossibleTypes,
        customFormsPossibleTypes,
        nodePossibleTypes,
        paymentMethodPossibleTypes,
      ),
      typePolicies: Object.assign(
        {},
        typePoliciesQueryFields,
        typePoliciesHiringFields,
        typePoliciesCompanyFields,
        typePoliciesCustomFormsFields,
        typePoliciesCompensationInsightsFields,
      ),
    }),
    connectToDevTools,
    credentials: "include",
    link: splitLink,
  })

  return client
}
